<template>
  <button
    v-if="copyable"
    type="button"
    class="vds-copyable vds-copyable--btn"
    :class="{ 'vds-copyable--icon-start': iconStart }"
    :aria-label="`Copy ${value}`"
    @click="copy"
  >
    <v-icon v-if="iconStart" x-small aria-hidden="true" class="vds-copyable__icon mr-1">mdi-content-copy</v-icon>
    <span class="vds-copyable__text">{{ display || value }}</span>
    <v-icon v-if="!iconStart" x-small aria-hidden="true" class="vds-copyable__icon ml-1">mdi-content-copy</v-icon>
  </button>
  <span v-else class="vds-copyable vds-copyable--static">{{ display || value }}</span>
</template>

<script>
import { mapActions } from 'vuex'

export default {
  name: 'KvmCopyable',
  props: {
    value: { type: [String, Number], default: '' },
    // Optional display text when it should differ from the copied value.
    display: { type: String, default: '' },
    // Put the (hover-only) copy icon BEFORE the text, so a right-aligned value
    // ends flush with its column like the plain values around it.
    iconStart: { type: Boolean, default: false },
  },
  computed: {
    // Nothing to copy for placeholders / empties.
    copyable () {
      return this.value !== '' && this.value != null && this.value !== '—'
    },
  },
  methods: {
    ...mapActions(['reportSuccess', 'reportError']),
    async copy () {
      if (!this.copyable) return
      const text = String(this.value)
      try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(text)
        } else if (!this.fallbackCopy(text)) {
          throw new Error('Clipboard unavailable')
        }
        this.reportSuccess('Copied')
      } catch (e) {
        // Clipboard API can reject on insecure contexts or denied permission —
        // try the legacy path before surfacing a failure.
        if (this.fallbackCopy(text)) {
          this.reportSuccess('Copied')
        } else {
          this.reportError('Could not copy to clipboard')
        }
      }
    },
    // execCommand fallback for insecure origins / older browsers.
    fallbackCopy (text) {
      try {
        const ta = document.createElement('textarea')
        ta.value = text
        ta.setAttribute('readonly', '')
        ta.style.position = 'absolute'
        ta.style.left = '-9999px'
        document.body.appendChild(ta)
        ta.select()
        const ok = document.execCommand('copy')
        document.body.removeChild(ta)
        return ok
      } catch (e) {
        return false
      }
    },
  },
}
</script>

<style scoped lang="scss">
@import './kvm-ui';

.vds-copyable--btn {
  @include kvm-min-target(24px);
  justify-content: flex-start;
  appearance: none;
  -webkit-appearance: none;
  background: transparent;
  border: 0;
  margin: 0;
  padding: 0 2px;
  font: inherit;
  line-height: inherit;
  color: inherit;
  text-align: inherit;
  vertical-align: baseline;
  cursor: pointer;
  border-radius: 4px;

  &:focus-visible {
    @include kvm-focus-ring;
  }
}

// Right-aligned use: no trailing padding either, so the text ends exactly at
// the column edge.
.vds-copyable--icon-start {
  padding-right: 0;
}

.vds-copyable__icon {
  opacity: 0;
  transition: opacity 0.15s;
}
.vds-copyable--btn:hover .vds-copyable__icon,
.vds-copyable--btn:focus-visible .vds-copyable__icon {
  opacity: 0.6;
}

@include kvm-reduced-motion {
  .vds-copyable__icon {
    transition: none;
  }
}
</style>
